import React, { useMemo, useState, useEffect } from 'react';
import Accordion from 'components/Accordion';
import DatePicker from 'components/DatePicker';
import TitleSemiBold from 'components/Typography/TitleSemiBold';
import usePeriodType from 'hooks/Meteorology/Fields/ModelPerformance/usePeriodType';
import useOptions from 'hooks/Meteorology/Fields/ModelPerformance/useOptions';
import { parse } from 'date-fns';
import usePresetRounds from 'hooks/Meteorology/Fields/ModelPerformance/usePresetRounds';

export default function Rounds() {
  const { periodType } = usePeriodType();
  const { availableRounds, minDate, maxDate } = useOptions();
  const {
    selectedRounds, setSelectedRounds, roundsRange, setRoundsRange,
  } = usePresetRounds();

  const [localStartDate, setLocalStartDate] = useState<Date | null>(null);
  const [localEndDate, setLocalEndDate] = useState<Date | null>(null);

  const isDisabled = periodType !== 'preset';

  const minDateObj = useMemo(() => {
    if (!minDate) return undefined;
    return parse(minDate, 'yyyy-MM-dd', new Date());
  }, [minDate]);

  const maxDateObj = useMemo(() => {
    if (!maxDate) return undefined;
    return parse(maxDate, 'yyyy-MM-dd', new Date());
  }, [maxDate]);

  useEffect(() => {
    if (periodType !== 'preset') {
      setLocalStartDate(null);
      setLocalEndDate(null);
    }
  }, [periodType]);

  useEffect(() => {
    if (selectedRounds.length === 0) return;
    const times = selectedRounds.map((d) => d.getTime());
    const start = new Date(Math.min(...times));
    const end = new Date(Math.max(...times));
    setLocalStartDate(start);
    setLocalEndDate(end);
  }, [selectedRounds]);

  // Corrige a corrida entre a selecao do intervalo e o carregamento das rodadas:
  // se o usuario escolheu o intervalo antes de `availableRounds` chegar, a
  // interseccao inicial fica vazia e `rounds` nunca e setado. Ao chegar a lista,
  // recomputa a interseccao a partir do intervalo ja selecionado.
  useEffect(() => {
    if (selectedRounds.length > 0) return;
    if (!localStartDate || !localEndDate) return;
    if (!availableRounds || availableRounds.length === 0) return;
    const startTime = localStartDate.getTime();
    const endTime = localEndDate.getTime();
    const inRange = availableRounds
      .map((roundStr) => parse(roundStr, 'yyyy-MM-dd', new Date()))
      .filter((d) => d.getTime() >= startTime && d.getTime() <= endTime);
    if (inRange.length > 0) {
      setSelectedRounds(inRange);
    }
  }, [availableRounds, localStartDate, localEndDate, selectedRounds, setSelectedRounds]);

  // O intervalo escolhido fica no Redux e sobrevive à troca de pêntada: o calendário
  // mostra ele, não só o trecho que tem rodadas na pêntada atual.
  const startDate = useMemo(() => {
    if (roundsRange) return parse(roundsRange.start, 'yyyy-MM-dd', new Date());
    if (localStartDate) return localStartDate;
    if (!selectedRounds.length) return null;
    return new Date(Math.min(...selectedRounds.map((d) => d.getTime())));
  }, [roundsRange, localStartDate, selectedRounds]);

  const endDate = useMemo(() => {
    if (roundsRange) return parse(roundsRange.end, 'yyyy-MM-dd', new Date());
    if (localEndDate) return localEndDate;
    if (!selectedRounds.length) return null;
    return new Date(Math.max(...selectedRounds.map((d) => d.getTime())));
  }, [roundsRange, localEndDate, selectedRounds]);

  const handleDateChange = (dates: (Date | null)[] | Date | null) => {
    if (!Array.isArray(dates)) {
      setLocalStartDate(null);
      setLocalEndDate(null);
      setSelectedRounds([]);
      setRoundsRange(null, null);
      return;
    }
    const [newStartDate, newEndDate] = dates;
    setLocalStartDate(newStartDate);
    setLocalEndDate(newEndDate);
    setRoundsRange(newStartDate, newEndDate);

    if (newStartDate && newEndDate) {
      const startTime = newStartDate.getTime();
      const endTime = newEndDate.getTime();
      const inRange = (availableRounds ?? [])
        .map((roundStr) => parse(roundStr, 'yyyy-MM-dd', new Date()))
        .filter((d) => d.getTime() >= startTime && d.getTime() <= endTime);
      setSelectedRounds(inRange);
    } else {
      setSelectedRounds([]);
    }
  };

  if (isDisabled) {
    return null;
  }

  return (
    <Accordion.Item eventKey="rounds" id="rounds-accordion">
      <Accordion.Header>
        <TitleSemiBold>Rodadas</TitleSemiBold>
      </Accordion.Header>
      <Accordion.Body>
        <DatePicker
          selectsRange
          startDate={startDate}
          endDate={endDate}
          onChange={handleDateChange}
          minDate={minDateObj}
          maxDate={maxDateObj}
          inline
        />
      </Accordion.Body>
    </Accordion.Item>
  );
}
